// Tabela de Preços — a casa única do preço (issue #7, ADR-0006). Aqui moram o
// preço base (vale para todos os Clientes) e o preço de Cliente, cada um com
// histórico de vigência. O preço de Cliente tem prioridade sobre o base.

import type { Id } from "./ids.ts";
import { hojeISO, type DataISO } from "./datas.ts";
import type { Centavos } from "./money.ts";
import type { PrecoBase, PrecoCliente, Snapshot } from "./snapshot.ts";

export class ErroPrecificacao extends Error {}

export type PrecoResolvido =
  | { pendente: true }
  | { pendente: false; valorCentavos: Centavos; origem: "cliente" | "base" };

function vigenteAte<T extends { desde: DataISO }>(
  itens: readonly T[],
  data: DataISO,
): T | null {
  let atual: T | null = null;
  for (const it of [...itens].sort((a, b) => a.desde.localeCompare(b.desde))) {
    if (it.desde <= data) atual = it;
  }
  return atual;
}

// -------------------------------------------------------------- preço base ----

/** É a vigência de preço base deste Tipo que começa em `desde`? */
const ehVigenciaBase =
  (tipoId: Id, desde: DataISO) =>
  (p: PrecoBase): boolean =>
    p.tipoId === tipoId && p.desde === desde;

/** O preço base vigente de um Tipo numa data (ou `null` se nenhuma vigência a alcança). */
export function precoBaseVigente(
  snapshot: Pick<Snapshot, "precosBase">,
  tipoId: Id,
  data: DataISO = hojeISO(),
): Centavos | null {
  const p = vigenteAte(
    snapshot.precosBase.filter((x) => x.tipoId === tipoId),
    data,
  );
  return p ? p.valorCentavos : null;
}

/**
 * Registra um preço base a partir de `desde`. Substitui a vigência se já houver
 * uma exatamente nessa data; senão, acrescenta ao histórico.
 */
export function definirPrecoBase(
  snapshot: Snapshot,
  tipoId: Id,
  valorCentavos: Centavos,
  desde: DataISO = hojeISO(),
): Snapshot {
  if (!snapshot.tiposPeca.some((t) => t.id === tipoId)) {
    throw new ErroPrecificacao("Tipo de peça não encontrado.");
  }
  if (!Number.isInteger(valorCentavos) || valorCentavos < 0) {
    throw new ErroPrecificacao("Preço inválido (use centavos, inteiro e não negativo).");
  }
  const mesma = ehVigenciaBase(tipoId, desde);
  const outros = snapshot.precosBase.filter((p) => !mesma(p));
  const novo: PrecoBase = { tipoId, valorCentavos, desde };
  return { ...snapshot, precosBase: [...outros, novo] };
}

/** Esvazia o histórico de preço base de um Tipo — passa a não ter preço próprio. */
export function limparPrecoBase(snapshot: Snapshot, tipoId: Id): Snapshot {
  if (!snapshot.tiposPeca.some((t) => t.id === tipoId)) {
    throw new ErroPrecificacao("Tipo de peça não encontrado.");
  }
  return {
    ...snapshot,
    precosBase: snapshot.precosBase.filter((p) => p.tipoId !== tipoId),
  };
}

/** Remove uma vigência de preço base (a que começa exatamente em `desde`). */
export function removerPrecoBaseVigencia(
  snapshot: Snapshot,
  tipoId: Id,
  desde: DataISO,
): Snapshot {
  const mesma = ehVigenciaBase(tipoId, desde);
  if (!snapshot.precosBase.some(mesma)) {
    throw new ErroPrecificacao("Não há vigência de preço base nessa data.");
  }
  return { ...snapshot, precosBase: snapshot.precosBase.filter((p) => !mesma(p)) };
}

// ---------------------------------------------------------- preço resolvido ----

/** Preço de `(Cliente, Tipo, data)`: preço do Cliente vigente › base vigente › pendente. */
export function precoVigente(
  snapshot: Pick<Snapshot, "precosCliente" | "precosBase" | "tiposPeca">,
  clienteId: Id,
  tipoId: Id,
  data: DataISO,
): PrecoResolvido {
  const doCliente = vigenteAte(
    snapshot.precosCliente.filter(
      (p) => p.clienteId === clienteId && p.tipoId === tipoId,
    ),
    data,
  );
  if (doCliente) {
    return { pendente: false, valorCentavos: doCliente.valorCentavos, origem: "cliente" };
  }
  const base = precoBaseVigente(snapshot, tipoId, data);
  if (base != null) return { pendente: false, valorCentavos: base, origem: "base" };
  return { pendente: true };
}

/**
 * Dos `tipoIds` dados, quais estão com **preço pendente** para o Cliente na data
 * (sem preço do Cliente e sem preço base). O fechamento de uma Fatura que inclua
 * qualquer um deles deve ser bloqueado, com alerta apontando os Tipos.
 */
export function tiposComPrecoPendente(
  snapshot: Pick<Snapshot, "precosCliente" | "precosBase" | "tiposPeca">,
  clienteId: Id,
  tipoIds: readonly Id[],
  data: DataISO,
): Id[] {
  return [...new Set(tipoIds)].filter(
    (tipoId) => precoVigente(snapshot, clienteId, tipoId, data).pendente,
  );
}

// ------------------------------------------------------------ preço cliente ----

/** É a vigência de preço deste Cliente × Tipo que começa em `desde`? */
const ehVigenciaCliente =
  (clienteId: Id, tipoId: Id, desde: DataISO) =>
  (p: PrecoCliente): boolean =>
    p.clienteId === clienteId && p.tipoId === tipoId && p.desde === desde;

/** Preço do Cliente vigente numa data (ignora o base). */
export function precoClienteVigente(
  snapshot: Pick<Snapshot, "precosCliente">,
  clienteId: Id,
  tipoId: Id,
  data: DataISO = hojeISO(),
): Centavos | null {
  const p = vigenteAte(
    snapshot.precosCliente.filter(
      (x) => x.clienteId === clienteId && x.tipoId === tipoId,
    ),
    data,
  );
  return p ? p.valorCentavos : null;
}

export function definirPrecoCliente(
  snapshot: Snapshot,
  clienteId: Id,
  tipoId: Id,
  valorCentavos: Centavos,
  desde: DataISO = hojeISO(),
): Snapshot {
  if (!snapshot.clientes.some((c) => c.id === clienteId)) {
    throw new ErroPrecificacao("Cliente não encontrado.");
  }
  if (!snapshot.tiposPeca.some((t) => t.id === tipoId)) {
    throw new ErroPrecificacao("Tipo de peça não encontrado.");
  }
  if (!Number.isInteger(valorCentavos) || valorCentavos < 0) {
    throw new ErroPrecificacao("Preço inválido (use centavos, inteiro e não negativo).");
  }

  const mesma = ehVigenciaCliente(clienteId, tipoId, desde);
  const outros = snapshot.precosCliente.filter((p) => !mesma(p));
  const novo: PrecoCliente = { clienteId, tipoId, valorCentavos, desde };
  return { ...snapshot, precosCliente: [...outros, novo] };
}

/**
 * Apaga **todo** o histórico de preço — base e de todos os Clientes. Botão
 * provisório da Tabela de Preços, para zerar e recomeçar durante o ajuste.
 */
export function limparTodosOsPrecos(snapshot: Snapshot): Snapshot {
  return { ...snapshot, precosBase: [], precosCliente: [] };
}

/** Remove todas as vigências de preço deste Cliente para este Tipo (volta ao base). */
export function limparPrecoCliente(
  snapshot: Snapshot,
  clienteId: Id,
  tipoId: Id,
): Snapshot {
  return {
    ...snapshot,
    precosCliente: snapshot.precosCliente.filter(
      (p) => !(p.clienteId === clienteId && p.tipoId === tipoId),
    ),
  };
}

/** Remove uma vigência de preço do Cliente (a que começa exatamente em `desde`). */
export function removerPrecoClienteVigencia(
  snapshot: Snapshot,
  clienteId: Id,
  tipoId: Id,
  desde: DataISO,
): Snapshot {
  const mesma = ehVigenciaCliente(clienteId, tipoId, desde);
  if (!snapshot.precosCliente.some(mesma)) {
    throw new ErroPrecificacao("Não há preço do cliente nessa data.");
  }
  return { ...snapshot, precosCliente: snapshot.precosCliente.filter((p) => !mesma(p)) };
}

// ------------------------------------------------------ vigências gravadas ----

export interface VigenciaPreco {
  desde: DataISO;
  valorCentavos: Centavos;
  /** É a que vale em `hoje` (a mais recente já começada)? */
  vigente: boolean;
}

/**
 * As vigências gravadas de um preço, mais recente primeiro: as do preço base
 * (`clienteId` null) ou só as do preço próprio do Cliente — sem misturar os
 * dois (a mistura é a `linhaDoTempoPreco`).
 */
export function vigenciasPreco(
  snapshot: Pick<Snapshot, "precosBase" | "precosCliente">,
  tipoId: Id,
  clienteId: Id | null,
  hoje: DataISO = hojeISO(),
): VigenciaPreco[] {
  const gravadas: { desde: DataISO; valorCentavos: Centavos }[] =
    clienteId == null
      ? snapshot.precosBase.filter((p) => p.tipoId === tipoId)
      : snapshot.precosCliente.filter((p) => p.clienteId === clienteId && p.tipoId === tipoId);
  const vigente = vigenteAte(gravadas, hoje);
  return [...gravadas]
    .sort((a, b) => b.desde.localeCompare(a.desde))
    .map((p) => ({ desde: p.desde, valorCentavos: p.valorCentavos, vigente: p === vigente }));
}

// ------------------------------------------------------ corrigir vigência ----

/**
 * Corrige uma vigência já gravada: a data em que ela começa (`de` → `para`) e
 * o valor. A mesma regra para o preço base e para o preço próprio do Cliente:
 * a vigência `de` precisa existir (quem recusa é o `remover`), e mudar a data
 * nunca sobrescreve outra vigência que já comece em `para`.
 */
function corrigirVigencia(
  existe: (desde: DataISO) => boolean,
  remover: (desde: DataISO) => Snapshot,
  definir: (s: Snapshot) => Snapshot,
  de: DataISO,
  para: DataISO,
): Snapshot {
  if (para !== de && existe(para)) throw new ErroPrecificacao("Já existe uma vigência nessa data.");
  return definir(remover(de));
}

export function corrigirVigenciaPrecoBase(
  snapshot: Snapshot,
  tipoId: Id,
  de: DataISO,
  para: DataISO,
  valorCentavos: Centavos,
): Snapshot {
  return corrigirVigencia(
    (d) => snapshot.precosBase.some(ehVigenciaBase(tipoId, d)),
    (d) => removerPrecoBaseVigencia(snapshot, tipoId, d),
    (s) => definirPrecoBase(s, tipoId, valorCentavos, para),
    de,
    para,
  );
}

export function corrigirVigenciaPrecoCliente(
  snapshot: Snapshot,
  clienteId: Id,
  tipoId: Id,
  de: DataISO,
  para: DataISO,
  valorCentavos: Centavos,
): Snapshot {
  return corrigirVigencia(
    (d) => snapshot.precosCliente.some(ehVigenciaCliente(clienteId, tipoId, d)),
    (d) => removerPrecoClienteVigencia(snapshot, clienteId, tipoId, d),
    (s) => definirPrecoCliente(s, clienteId, tipoId, valorCentavos, para),
    de,
    para,
  );
}

// -------------------------------------------------------------- linha do tempo ----

export interface SegmentoPreco {
  /** Data em que este preço passou a valer. */
  desde: DataISO;
  /** `null` = sem preço aplicável no período (pendente). */
  valorCentavos: Centavos | null;
  origem: "base" | "cliente";
  /** É o segmento que vale hoje. */
  vigente: boolean;
}

/**
 * Linha do tempo do preço efetivo de um Tipo: só o preço base
 * (`clienteId` nulo) ou já mesclado com o preço do Cliente por cima (ADR-0002).
 * Segmentos em ordem cronológica; períodos consecutivos de mesmo valor e mesma
 * origem são unidos.
 */
export function linhaDoTempoPreco(
  snapshot: Pick<Snapshot, "precosCliente" | "precosBase" | "tiposPeca">,
  tipoId: Id,
  clienteId: Id | null,
  hoje: DataISO = hojeISO(),
): SegmentoPreco[] {
  if (!snapshot.tiposPeca.some((t) => t.id === tipoId)) return [];

  const datas = new Set<DataISO>(
    snapshot.precosBase.filter((p) => p.tipoId === tipoId).map((p) => p.desde),
  );
  if (clienteId) {
    for (const p of snapshot.precosCliente) {
      if (p.clienteId === clienteId && p.tipoId === tipoId) datas.add(p.desde);
    }
  }

  const bruto = [...datas]
    .sort()
    .map((desde): SegmentoPreco => {
      if (clienteId) {
        const r = precoVigente(snapshot, clienteId, tipoId, desde);
        return r.pendente
          ? { desde, valorCentavos: null, origem: "base", vigente: false }
          : {
              desde,
              valorCentavos: r.valorCentavos,
              origem: r.origem,
              vigente: false,
            };
      }
      return {
        desde,
        valorCentavos: precoBaseVigente(snapshot, tipoId, desde),
        origem: "base",
        vigente: false,
      };
    });

  const segmentos: SegmentoPreco[] = [];
  for (const s of bruto) {
    const ant = segmentos[segmentos.length - 1];
    if (ant && ant.valorCentavos === s.valorCentavos && ant.origem === s.origem) {
      continue;
    }
    segmentos.push(s);
  }

  for (let i = segmentos.length - 1; i >= 0; i--) {
    if (segmentos[i]!.desde <= hoje) {
      segmentos[i]!.vigente = true;
      break;
    }
  }
  return segmentos;
}
